import { useEffect, useState } from "react";
import { motion, useMotionValue, animate } from "framer-motion";
import { Check, Minus, Loader2, Crown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SponsorCheckoutModal } from "@/components/SponsorCheckoutModal";

const inputCls = "rounded-xl border border-neutral-700 bg-black px-4 py-3 text-sm text-stone-100 outline-none focus:border-cyan-500";
const fmtK = (n) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1000 ? `${Math.round(n / 1000)}K` : `${n}`);

function Counter({ value, format = fmtK, testId }) {
  const mv = useMotionValue(0);
  const [v, setV] = useState("0");
  useEffect(() => {
    const c = animate(mv, value, { duration: 1.4, ease: "easeOut", onUpdate: (x) => setV(format(x)) });
    return () => c.stop();
  }, [value, mv, format]);
  return <span data-testid={testId}>{v}</span>;
}

function Cell({ v }) {
  if (v === "—") return <Minus className="mx-auto h-4 w-4 text-stone-600" />;
  return <span className="flex items-start gap-2 text-sm text-neutral-300"><Check className="mt-0.5 h-4 w-4 shrink-0 text-cyan-500" />{v}</span>;
}

function TierCard({ t, features, onSelect }) {
  const title = t.id === "title";
  return (
    <motion.div id={t.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      className={`flex w-[85vw] shrink-0 snap-center flex-col rounded-3xl p-6 md:w-auto md:shrink ${title ? "glass ring-gold shimmer-gold border-cyan-500/40" : "glass"}`}
      data-testid={`tier-card-${t.id}`}>
      <div className="flex items-center justify-between">
        <span className="font-mono-label text-[10px] uppercase tracking-widest text-neutral-400">{t.name}</span>
        {title && <span className="flex items-center gap-1 rounded-full border border-cyan-500/50 bg-cyan-950/50 px-2.5 py-1 font-mono-label text-[9px] uppercase tracking-wider text-cyan-300"><Crown className="h-3 w-3" /> {t.limited}</span>}
      </div>
      <div className={`mt-2 font-display text-5xl ${title ? "text-gold" : "text-chrome"}`}>${fmtK(t.price)}</div>
      <p className="mt-1 font-cond text-base uppercase tracking-wide text-neutral-400">{t.tagline}</p>

      <div className="mt-6 grid grid-cols-2 gap-3 rounded-2xl border border-white/5 bg-black/30 p-4">
        {[["Impressions", t.roi.impressions, fmtK], ["Reach", t.roi.reach, fmtK], ["CPM", t.roi.cpm, (x) => `$${x.toFixed(1)}`], ["Media Value", t.roi.media_value, (x) => `$${fmtK(x)}`]].map(([l, v, f]) => (
          <div key={l}>
            <div className="font-display text-xl text-cyan-400"><Counter value={v} format={f} testId={`roi-${t.id}-${l.toLowerCase().replace(" ", "-")}`} /></div>
            <div className="font-mono-label text-[9px] uppercase tracking-widest text-neutral-500">{l}</div>
          </div>
        ))}
        <div className="col-span-2 font-mono-label text-[9px] uppercase tracking-widest text-stone-600">Projected per event</div>
      </div>

      <ul className="mt-6 flex-1 space-y-3">
        {features.map((f, i) => (
          <li key={f}>
            <div className="font-mono-label text-[9px] uppercase tracking-widest text-neutral-500">{f}</div>
            <div className="mt-0.5"><Cell v={t.matrix[i]} /></div>
          </li>
        ))}
      </ul>

      <button onClick={() => onSelect(t)} data-testid={`tier-secure-${t.id}`}
        className={`${title ? "btn-gold" : "btn-chrome"} mt-6 w-full rounded-full py-3 font-cond font-bold uppercase`}>
        Secure This Tier · 25% deposit
      </button>
      <button onClick={() => onSelect(t, true)} data-testid={`tier-deck-${t.id}`}
        className="mt-2 w-full rounded-full border border-white/10 py-2.5 font-cond text-sm font-bold uppercase text-neutral-300 hover:border-cyan-500 hover:text-cyan-400">
        Request Sponsor Deck
      </button>
    </motion.div>
  );
}

export default function SponsorshipsPage() {
  const [tiers, setTiers] = useState([]);
  const [features, setFeatures] = useState([]);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", tier: "", target_event: "", budget: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [checkoutTier, setCheckoutTier] = useState(null);

  useEffect(() => {
    api.get("/sponsorship-tiers").then((r) => { setTiers(r.data.tiers); setFeatures(r.data.features); }).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data.filter((e) => new Date(e.date) >= Date.now()))).catch(() => {});
    if (new URLSearchParams(window.location.search).get("cancelled")) toast("Checkout cancelled — your tier was not reserved.");
  }, []);

  const select = (t, deck = false) => {
    if (!deck) return setCheckoutTier(t);
    setForm((f) => ({ ...f, tier: t.name, budget: `$${fmtK(t.price)}`, message: `Please send the ${t.name} sponsor deck.` }));
    document.getElementById("sponsor-form")?.scrollIntoView({ behavior: "smooth" });
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) return toast.error("Name, email and company are required.");
    setBusy(true);
    try {
      await api.post("/sponsors", { ...form, package: form.tier });
      toast.success("Request received — the partnerships team will reach out within one business day.");
      setForm({ name: "", email: "", phone: "", company: "", tier: "", target_event: "", budget: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 md:py-20" data-testid="sponsorships-page">
      <SponsorCheckoutModal tier={checkoutTier} events={events} onClose={() => setCheckoutTier(null)} />
      <div className="max-w-3xl">
        <span className="eyebrow">Sponsorship Matrix</span>
        <h1 className="mt-2 font-display text-4xl uppercase text-chrome sm:text-5xl lg:text-6xl">Partner. Gold. Platinum. <span className="text-gold">Title.</span></h1>
        <p className="mt-4 text-neutral-300">
          Four tiers engineered for brand managers who measure outcomes. Every line below shows what you get and what it's projected to return — across trail rides, concerts, corporate and arena events.
        </p>
      </div>

      <div className="mt-12 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-4 md:grid md:grid-cols-4 md:overflow-visible" data-testid="tier-matrix">
        {tiers.map((t) => <TierCard key={t.id} t={t} features={features} onSelect={select} />)}
      </div>
      <p className="mt-3 font-mono-label text-[10px] uppercase tracking-widest text-stone-600">ROI figures are projected estimates until post-event reports are published. Swipe on mobile.</p>

      <div id="sponsor-form" className="glass mt-16 rounded-3xl p-6 md:p-10">
        <span className="eyebrow">Request a deck or custom package</span>
        <h2 className="mt-2 font-display text-3xl uppercase text-chrome">Talk To Partnerships</h2>
        <form onSubmit={submit} className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="sponsor-request-form">
          <input required placeholder="Company / brand" value={form.company} onChange={set("company")} data-testid="sponsor-company" className={inputCls} />
          <input required placeholder="Contact name" value={form.name} onChange={set("name")} data-testid="sponsor-name" className={inputCls} />
          <input required type="email" placeholder="Work email" value={form.email} onChange={set("email")} data-testid="sponsor-email" className={inputCls} />
          <input placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="sponsor-phone" className={inputCls} />
          <select value={form.tier} onChange={set("tier")} data-testid="sponsor-tier-select" className={inputCls}>
            <option value="">Select a tier</option>
            {tiers.map((t) => <option key={t.id} value={t.name}>{`${t.name} — $${fmtK(t.price)}`}</option>)}
          </select>
          <select value={form.target_event} onChange={set("target_event")} data-testid="sponsor-event-select" className={inputCls}>
            <option value="">Target event (optional)</option>
            {events.map((e) => <option key={e.slug} value={e.title}>{e.title}</option>)}
            <option value="Multi-event / season">Multi-event / season</option>
          </select>
          <input placeholder="Budget range" value={form.budget} onChange={set("budget")} data-testid="sponsor-budget" className={`${inputCls} md:col-span-2`} />
          <textarea placeholder="Objectives, activation ideas, timing…" value={form.message} onChange={set("message")} data-testid="sponsor-message" rows={4} className={`${inputCls} md:col-span-2`} />
          <button type="submit" disabled={busy} data-testid="sponsor-request-submit" className="btn-gold flex items-center justify-center gap-2 rounded-full py-4 font-display text-lg uppercase disabled:opacity-60 md:col-span-2">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send Request"}
          </button>
        </form>
      </div>
    </div>
  );
}
